import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PlatformAdminStatus, CreatePlatformAdminRequest, PlatformAdminListItem } from '@resolve-ai/shared';
import { platformAdminsApi } from '../lib/platform-admins-api';
import { ApiError } from '../lib/api-client';

const EMPTY_ADMINS: PlatformAdminListItem[] = [];

interface ToastState {
  isOpen: boolean;
  message: string;
  variant: 'success' | 'error' | 'warning' | 'info';
}

const EMPTY_TOAST: ToastState = { isOpen: false, message: '', variant: 'info' };

export function usePlatformAdminsPage() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toast, setToast] = useState<ToastState>(EMPTY_TOAST);

  const adminsQuery = useQuery({
    queryKey: ['platform-admins'],
    queryFn: () => platformAdminsApi.list(),
  });

  const admins = adminsQuery.data?.admins ?? EMPTY_ADMINS;

  const showToast = (message: string, variant: ToastState['variant']) =>
    setToast({ isOpen: true, message, variant });

  const createMutation = useMutation({
    mutationFn: (payload: CreatePlatformAdminRequest) => platformAdminsApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-admins'] });
      setShowCreateModal(false);
      showToast('Admin added successfully', 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof ApiError ? error.message : 'Failed to add admin', 'error');
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: PlatformAdminStatus }) =>
      platformAdminsApi.updateStatus(id, status),
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['platform-admins'] });
      showToast(status === 'active' ? 'Admin activated' : 'Admin deactivated', 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof ApiError ? error.message : 'Failed to update admin', 'error');
    },
  });

  return {
    query: {
      isLoading: adminsQuery.isLoading,
      hasError: adminsQuery.isError,
      admins,
    },
    modal: {
      isOpen: showCreateModal,
      open: () => setShowCreateModal(true),
      close: () => setShowCreateModal(false),
    },
    mutations: { create: createMutation, updateStatus: updateStatusMutation },
    toast: { ...toast, close: () => setToast(EMPTY_TOAST) },
  };
}
